<template>
  <main-template>
    <v-container fluid class="pa-0">
      <v-row>
        <v-col cols="12">
          <div class="d-flex flex-wrap align-center justify-space-between">
            <div>
              <h1 class="mb-2">
                <v-btn color="primary" x-small text @click="goBack">
                  <v-icon left> fa fa-angle-left </v-icon>
                </v-btn>
                Presença no Notifiq
              </h1>
              <div class="grey--text text--darken-1">
                Acompanhamento automático das presenças registradas hoje.
              </div>
            </div>
            <div class="d-flex align-center mt-3 mt-md-0">
              <v-chip class="mr-2" color="green lighten-5" text-color="green darken-2" label>
                {{ totalEventos }} eventos
              </v-chip>
              <v-btn color="primary" :loading="refreshing" @click="refresh({ silent: false })">
                <v-icon left>mdi-refresh</v-icon>
                Atualizar agora
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="3">
          <v-card outlined>
            <v-card-text>
              <div class="text-overline">Data</div>
              <div class="text-h6">{{ formattedDate }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined>
            <v-card-text>
              <div class="text-overline">Última atualização</div>
              <div class="text-h6">{{ formattedGeneratedAt }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined>
            <v-card-text>
              <div class="text-overline">Escolas com movimento</div>
              <div class="text-h6">{{ totalEscolas }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card outlined>
            <v-card-text>
              <div class="text-overline">Turmas com movimento</div>
              <div class="text-h6">{{ totalTurmas }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-alert border="left" colored-border dense outlined type="info" class="mb-0">
            {{ descricaoEscopoVisualizacao }}
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card outlined :loading="loading">
            <v-card-text v-if="!loading && !escolas.length" class="py-10 text-center">
              <v-icon size="42" color="grey lighten-1">mdi-bell-off-outline</v-icon>
              <div class="text-h6 mt-4">Nenhum evento registrado hoje.</div>
              <div class="grey--text text--darken-1">
                Assim que o Hikvision inserir uma nova presença, ela aparecerá aqui automaticamente.
              </div>
            </v-card-text>

            <v-card-text v-else class="pa-2 pa-md-4">
              <v-expansion-panels v-model="openPanels" multiple accordion>
                <v-expansion-panel v-for="escola in escolas" :key="`escola-${escola.escola_id}`">
                  <v-expansion-panel-header>
                    <div class="d-flex flex-wrap align-center justify-space-between w-100 pr-4">
                      <div class="text-subtitle-1 font-weight-bold">
                        {{ escola.escola }}
                      </div>
                      <v-chip small color="primary" outlined>
                        {{ escola.turmas.length }} turma(s)
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-expansion-panels
                      v-model="turmaOpenPanels[escola.escola_id]"
                      multiple
                      class="turma-expansion"
                    >
                      <v-expansion-panel
                        v-for="turma in escola.turmas"
                        :key="`turma-${turma.turma_id}`"
                        class="mb-3"
                      >
                        <v-expansion-panel-header>
                          <div
                            class="d-flex justify-space-between align-center flex-wrap w-100 pr-4"
                          >
                            <span class="font-weight-medium">{{ turma.turma }}</span>
                            <v-chip small color="green lighten-5" text-color="green darken-2">
                              {{ turma.alunos.length }} evento(s)
                            </v-chip>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-list two-line>
                            <v-list-item
                              v-for="aluno in turma.alunos"
                              :key="`evento-${aluno.id}`"
                              class="aluno-list-item px-2 py-2"
                            >
                              <v-list-item-avatar
                                size="34"
                                class="aluno-avatar mr-2"
                                :color="
                                  aluno.tipo_evento === 'entrada'
                                    ? 'green lighten-5'
                                    : 'blue-grey lighten-5'
                                "
                              >
                                <v-icon
                                  size="18"
                                  :color="
                                    aluno.tipo_evento === 'entrada'
                                      ? 'green darken-2'
                                      : 'blue-grey darken-1'
                                  "
                                >
                                  {{
                                    aluno.tipo_evento === "entrada"
                                      ? "mdi-check-circle"
                                      : "mdi-exit-run"
                                  }}
                                </v-icon>
                              </v-list-item-avatar>
                              <v-list-item-content>
                                <v-list-item-title class="font-weight-medium text-wrap aluno-nome">
                                  {{ aluno.nome }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="aluno-meta">
                                  ID {{ aluno.aluno_id }} | Matrícula {{ aluno.matricula_id }}
                                </v-list-item-subtitle>
                              </v-list-item-content>
                              <v-list-item-action class="aluno-action my-0 ml-2 align-self-start">
                                <v-chip
                                  small
                                  label
                                  class="aluno-status mb-1"
                                  :color="aluno.tipo_evento === 'entrada' ? 'green' : 'blue-grey'"
                                  text-color="white"
                                >
                                  {{ aluno.tipo_evento === "entrada" ? "Entrada" : "Saída" }}
                                </v-chip>
                                <small class="grey--text text--darken-1 aluno-horario">
                                  {{ formatDateTime(aluno.data_evento) }}
                                </small>
                              </v-list-item-action>
                            </v-list-item>
                          </v-list>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";

const POLLING_INTERVAL = 15000;
const ROLES_COM_VISAO_GERAL = ["admin", "super-admin", "secretario"];

export default {
  name: "IndexNotifiqPage",
  data() {
    return {
      loading: true,
      refreshing: false,
      poller: null,
      escolas: [],
      date: null,
      generatedAt: null,
      totalEventos: 0,
      totalEscolas: 0,
      totalTurmas: 0,
      openPanels: [],
      turmaOpenPanels: {},
      panelsInitialized: false,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    formattedDate() {
      return this.date ? this.formatDate(this.date) : "--/--/----";
    },
    formattedGeneratedAt() {
      return this.generatedAt ? this.formatDateTime(this.generatedAt) : "Aguardando dados";
    },
    podeVerPresencasTodasEscolas() {
      return this.user?.roles?.some((role) => ROLES_COM_VISAO_GERAL.includes(role.name));
    },
    franquiasPermitidasIds() {
      const franquiasPermitidas = this.user?.franquias_permitidas;

      if (Array.isArray(franquiasPermitidas)) {
        return franquiasPermitidas.map(Number).filter(Boolean);
      }

      if (typeof franquiasPermitidas === "string") {
        try {
          const parsed = JSON.parse(franquiasPermitidas);
          return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
        } catch (error) {
          return [];
        }
      }

      return [];
    },
    franquiasRestritasIds() {
      const franquiaAtualId = Number(this.user?.franquia?.id);
      const franquiasPermitidasIds = this.franquiasPermitidasIds;

      if (franquiasPermitidasIds.length) {
        return franquiasPermitidasIds;
      }

      return franquiaAtualId ? [franquiaAtualId] : [];
    },
    descricaoEscopoVisualizacao() {
      if (this.podeVerPresencasTodasEscolas) {
        return "Visualizando presenças de todas as escolas.";
      }

      if (this.franquiasRestritasIds.length > 1) {
        return `Visualizando presenças das escolas vinculadas ao usuário (${this.franquiasRestritasIds.length} unidades).`;
      }

      return `Visualizando presenças da escola vinculada ao usuário: ${
        this.user?.franquia?.descricao || "unidade permitida"
      }.`;
    },
  },
  async mounted() {
    await this.refresh({ silent: false });
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    goBack() {
      if (this.user.role.name.includes("professor")) {
        this.$router.push({
          name: "acessoProfessor.home",
        });
      }
      this.$router.push({
        name: "home",
      });
    },
    startPolling() {
      this.stopPolling();
      this.poller = setInterval(() => {
        this.refresh({ silent: true });
      }, POLLING_INTERVAL);
    },
    stopPolling() {
      if (this.poller) {
        clearInterval(this.poller);
        this.poller = null;
      }
    },
    async refresh({ silent }) {
      if (silent) {
        this.refreshing = true;
      } else {
        this.loading = true;
      }

      try {
        const response = await this.$services.presencasService.listPresencasNotifiq();
        const escolasFiltradas = this.filtrarEscolasPorPerfil(response.escolas || []);
        const totais = this.calcularTotais(escolasFiltradas);

        this.escolas = escolasFiltradas;
        this.date = response.date || null;
        this.generatedAt = response.generated_at || null;
        this.totalEventos = totais.totalEventos;
        this.totalEscolas = totais.totalEscolas;
        this.totalTurmas = totais.totalTurmas;

        if (!this.panelsInitialized && this.escolas.length) {
          this.openPanels = this.escolas.map((_, index) => index);
          this.panelsInitialized = true;
        } else if (!this.escolas.length) {
          this.openPanels = [];
          this.panelsInitialized = false;
        }

        this.escolas.forEach((escola) => {
          if (!Array.isArray(this.turmaOpenPanels[escola.escola_id])) {
            this.$set(
              this.turmaOpenPanels,
              escola.escola_id,
              escola.turmas.map((_, index) => index)
            );
          }
        });
      } catch (error) {
        if (!silent) {
          this.$handleError(error);
        }
      } finally {
        this.loading = false;
        this.refreshing = false;
      }
    },
    filtrarEscolasPorPerfil(escolas) {
      if (this.podeVerPresencasTodasEscolas) {
        return escolas;
      }

      const franquiasPermitidasIds = this.franquiasRestritasIds;

      if (!franquiasPermitidasIds.length) {
        return [];
      }

      return escolas.filter((escola) => franquiasPermitidasIds.includes(Number(escola.escola_id)));
    },
    calcularTotais(escolas) {
      const totalTurmas = escolas.reduce((total, escola) => {
        return total + (escola.turmas?.length || 0);
      }, 0);

      const totalEventos = escolas.reduce((total, escola) => {
        const eventosDaEscola = (escola.turmas || []).reduce((turmaTotal, turma) => {
          return turmaTotal + (turma.alunos?.length || 0);
        }, 0);

        return total + eventosDaEscola;
      }, 0);

      return {
        totalEventos,
        totalEscolas: escolas.length,
        totalTurmas,
      };
    },
    formatDate(value) {
      if (!value) return "--/--/----";

      const date = this.parseDateValue(value);
      if (Number.isNaN(date.getTime())) return "--/--/----";

      return date.toLocaleDateString("pt-BR");
    },
    formatDateTime(value) {
      if (!value) return "--";

      const date = this.parseDateValue(value);
      if (Number.isNaN(date.getTime())) return "--";

      return date.toLocaleString("pt-BR");
    },
    parseDateValue(value) {
      if (!value) return new Date(NaN);

      if (value instanceof Date) {
        return value;
      }

      const normalizedValue = String(value).trim();
      const match = normalizedValue.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
      );

      if (match) {
        const [, year, month, day, hour = "00", minute = "00", second = "00"] = match;

        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second)
        );
      }

      return new Date(normalizedValue);
    },
  },
};
</script>

<style scoped>
.w-100 {
  width: 100%;
}

.turma-expansion {
  background: transparent;
}

.aluno-list-item {
  align-items: flex-start;
  min-height: 72px;
}

.aluno-avatar {
  min-width: 34px;
  margin-top: 2px;
}

.aluno-nome {
  font-size: 0.88rem;
  line-height: 1.25rem;
}

.aluno-meta {
  font-size: 0.76rem;
  line-height: 1.05rem;
  white-space: normal;
}

.aluno-action {
  min-width: 92px;
  align-items: flex-end;
}

.aluno-status {
  font-size: 0.72rem;
}

.aluno-horario {
  font-size: 0.72rem;
  line-height: 0.95rem;
  text-align: right;
  max-width: 92px;
  white-space: normal;
}

@media (max-width: 600px) {
  .aluno-list-item {
    min-height: 78px;
  }

  .aluno-nome {
    font-size: 0.82rem;
    line-height: 1.15rem;
  }

  .aluno-meta {
    font-size: 0.74rem;
    line-height: 1rem;
  }

  .aluno-action {
    min-width: 84px;
  }

  .aluno-status {
    font-size: 0.68rem;
  }

  .aluno-horario {
    font-size: 0.7rem;
    line-height: 0.9rem;
    max-width: 84px;
  }
}
</style>
